import React, { useEffect, useState } from 'react';
import { cn } from '../../../lib/utils';
import { Card, Skel } from './parts';
import { int, mins, Visitors } from './api';

const COLORS = ['var(--oa-accent)', 'var(--oa-s3)', 'var(--oa-s2)', 'var(--oa-accent-2)', 'var(--oa-muted)', 'var(--oa-faint)'];

/** How sessions arrived (PostHog's channel type), where they landed, and which outside sites sent them. */
export const SourcesCard: React.FC<{ v: Visitors | null; loading: boolean }> = ({ v, loading }) => {
  const [focus, setFocus] = useState<number | null>(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setReady(true), 2200);
    return () => window.clearTimeout(t);
  }, []);

  if (!v) {
    return (
      <Card i={5} area="src" title="Where visitors come from">
        {loading ? (
          <>
            <Skel className="h-[22px] w-full" />
            <Skel className="min-h-[180px] flex-1" />
          </>
        ) : (
          <p className="oa-cap">Visitor sources appear once PostHog is connected.</p>
        )}
      </Card>
    );
  }

  const total = Math.max(1, v.channels.reduce((a, c) => a + c.sessions, 0));
  const shown = v.channels[focus ?? 0];
  const referrers = v.topSources.filter((s) => s.source !== 'Direct');
  const landing = v.landing ?? [];
  const landMax = Math.max(1, ...landing.map((l) => l.sessions));

  return (
    <Card i={5} area="src" title="Where visitors come from">
      {v.channels.length === 0 ? (
        <p className="oa-cap flex flex-1 items-center">No visits in this period.</p>
      ) : (
        <>
          <div className="flex flex-col gap-2.5">
            <div className={cn('oa-split', focus !== null && 'is-focus', ready && 'is-ready')} role="group" aria-label="Sessions by channel" onMouseLeave={() => setFocus(null)}>
              {v.channels.map((c, i) => (
                <span
                  key={c.channel}
                  className={cn(focus === i && 'is-on')}
                  style={{ ['--v' as string]: Math.max((c.sessions / total) * 100, 1).toFixed(1), background: COLORS[i % COLORS.length] } as React.CSSProperties}
                  onMouseEnter={() => setFocus(i)}
                />
              ))}
            </div>
            {/* Detail for the hovered channel (the largest otherwise). */}
            {shown ? (
              <p className="text-[12.5px] text-[var(--oa-muted)]">
                <b className="text-[var(--oa-text)]">{shown.channel}</b> · {int(shown.sessions)} sessions · bounce {Math.round((shown.bounces / Math.max(1, shown.sessions)) * 100)}% · {mins(shown.duration / Math.max(1, shown.sessions))} on site
              </p>
            ) : null}
          </div>
          <ul className="oa-legend-list">
            {v.channels.map((c, i) => (
              <li
                key={c.channel}
                className={cn(focus === i && 'is-on')}
                title={c.channel === 'Internal' ? 'Visits that began on one of the site’s own pages, such as a link opened in a new tab' : undefined}
                onMouseEnter={() => setFocus(i)}
                onMouseLeave={() => setFocus(null)}
              >
                <i style={{ background: COLORS[i % COLORS.length] }} />
                <span className="truncate">{c.channel}</span>
                <em>{int(c.sessions)}</em>
                <b>{Math.round((c.sessions / total) * 100)}%</b>
              </li>
            ))}
          </ul>
          <div className="flex flex-1 flex-col gap-2.5">
            <div className="flex items-baseline justify-between gap-3">
              <h3 className="oa-sub">Landing pages</h3>
              <span className="text-[11.5px] text-[var(--oa-faint)]">where visits start</span>
            </div>
            {landing.length === 0 ? (
              <p className="oa-cap">No visits in this period.</p>
            ) : (
              <ol className="oa-bars flex-1">
                {landing.map((l, i) => (
                  <li key={l.path} style={{ ['--i' as string]: i } as React.CSSProperties}>
                    <span className="truncate">{l.path === '/' ? 'Home' : l.path}</span>
                    <b>{Math.round((l.sessions / total) * 100)}%</b>
                    <i style={{ ['--w' as string]: `${(l.sessions / landMax) * 100}%` } as React.CSSProperties} />
                    <em>
                      {int(l.sessions)} · bounce {Math.round((l.bounces / Math.max(1, l.sessions)) * 100)}%
                    </em>
                  </li>
                ))}
              </ol>
            )}
          </div>
          <p className="truncate text-[12px] text-[var(--oa-faint)]" title={referrers.map((s) => `${s.source}: ${int(s.sessions)} sessions`).join('\n')}>
            {referrers.length ? (
              <>
                Outside sites:{' '}
                {referrers.map((s, i) => (
                  <React.Fragment key={s.source}>
                    {i ? ', ' : ''}
                    <span className="text-[var(--oa-muted)]">{s.source}</span> {int(s.sessions)}
                  </React.Fragment>
                ))}
              </>
            ) : (
              'No outside sites have sent visitors yet.'
            )}
          </p>
        </>
      )}
    </Card>
  );
};
